import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

interface CompetitorAnalysis {
  id: string;
  competitor_url: string;
  competitor_title: string | null;
  competitor_description: string | null;
  keyword_gaps: string[];
  checked_at: string;
}

const CompetitorsTab = ({ clientIds }: { clientIds: string[] }) => {
  const [competitors, setCompetitors] = useState<CompetitorAnalysis[]>([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  const fetchCompetitors = useCallback(async () => {
    if (clientIds.length === 0) {
      setCompetitors([]);
      return;
    }
    const { data } = await supabase
      .from("competitor_analysis")
      .select("*")
      .in("client_id", clientIds)
      .order("checked_at", { ascending: false });
    if (data) setCompetitors(data as any);
  }, [clientIds]);

  useEffect(() => {
    if (clientIds.length === 0) {
      setLoading(false);
      return;
    }
    (async () => {
      await fetchCompetitors();
      setLoading(false);
    })();
  }, [clientIds, fetchCompetitors]);

  const handleRunAnalysis = async () => {
    if (clientIds.length === 0) return;
    setRunning(true);
    try {
      const { data, error } = await supabase.functions.invoke("analyze-competitors", {
        body: { client_id: clientIds[0] },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      toast({
        title: "Competitor analysis complete",
        description: `Found ${data?.competitors?.length ?? 0} competitors.`,
      });
      await fetchCompetitors();
    } catch (err: any) {
      toast({
        title: "Analysis failed",
        description: err.message || "Could not run competitor analysis.",
        variant: "destructive",
      });
    } finally {
      setRunning(false);
    }
  };

  const allGaps = Array.from(
    new Set(competitors.flatMap((c) => c.keyword_gaps || []))
  );

  const lastChecked = competitors[0]?.checked_at;

  const RunButton = (
    <button
      type="button"
      onClick={handleRunAnalysis}
      disabled={running || clientIds.length === 0}
      className="text-[11px] font-mono px-3 py-1.5 rounded-md bg-[#00D97E] text-black font-semibold hover:bg-[#00D97E]/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
    >
      {running ? "Running…" : "Run Analysis"}
    </button>
  );

  if (loading) {
    return (
      <div className="space-y-3">
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-20 bg-white/[0.02] rounded-lg animate-pulse border border-white/5" />
        ))}
      </div>
    );
  }

  if (competitors.length === 0) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-xs font-mono text-white/40 uppercase tracking-[0.15em]">Competitors</h3>
          {RunButton}
        </div>
        <div className="bg-[#0a0a0a] border border-[#1a1a1a] rounded-lg p-6 text-center">
          <p className="text-sm text-white/50 font-mono">No competitor analysis yet.</p>
          <p className="text-xs text-white/20 font-mono mt-2">Click "Run Analysis" to scan competitors for this client.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        {lastChecked ? (
          <p className="text-[10px] font-mono text-white/30">
            Last analyzed: {new Date(lastChecked).toLocaleDateString()} at{" "}
            {new Date(lastChecked).toLocaleTimeString()}
          </p>
        ) : <span />}
        {RunButton}
      </div>

      {/* Competitor cards */}
      <div className="space-y-2">
        <h3 className="text-xs font-mono text-white/40 uppercase tracking-[0.15em]">Top Competitors</h3>
        {competitors.map((comp, i) => {
          const domain = (() => {
            try { return new URL(comp.competitor_url).hostname; } catch { return comp.competitor_url; }
          })();

          return (
            <motion.div
              key={comp.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              className="bg-[#0a0a0a] border border-[#1a1a1a] rounded-lg p-3"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white/80 font-mono truncate">
                    {comp.competitor_title || domain}
                  </p>
                  <p className="text-[10px] text-white/30 font-mono truncate mt-0.5">
                    {domain}
                  </p>
                  {comp.competitor_description && (
                    <p className="text-[10px] text-white/20 font-mono mt-1 line-clamp-2">
                      {comp.competitor_description}
                    </p>
                  )}
                </div>
                <span className="shrink-0 text-[10px] font-mono px-2 py-0.5 rounded-full bg-[rgba(0,217,126,0.10)] text-[#00D97E]">
                  #{i + 1}
                </span>
              </div>
              {(comp.keyword_gaps || []).length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {comp.keyword_gaps.slice(0, 3).map((gap, gi) => (
                    <span
                      key={gi}
                      className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-[rgba(0,217,126,0.10)] text-[#00D97E]/60 truncate max-w-[150px]"
                    >
                      {gap}
                    </span>
                  ))}
                </div>
              )}
            </motion.div>
          );
        })}
      </div>

      {/* Keyword Gaps */}
      {allGaps.length > 0 && (
        <div>
          <h3 className="text-xs font-mono text-white/40 uppercase tracking-[0.15em] mb-2">
            Keyword Gaps ({allGaps.length})
          </h3>
          <div className="bg-[#0a0a0a] border border-[#1a1a1a] rounded-lg p-3 space-y-1.5">
            {allGaps.map((gap, i) => (
              <div key={i} className="flex items-center gap-2">
                <span className="text-[10px] text-[#00ff88]/50">→</span>
                <span className="text-xs font-mono text-white/60 truncate">{gap}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CompetitorsTab;
